import { useEffect, useState, useRef } from "react";
import { CircleMarker, useMap, useMapEvents } from "react-leaflet";
import axios from "axios";
import { API } from "./utils";

// Iter 82 — NYC fire-hydrant overlay layer.
//
// Renders every hydrant inside the visible map bounds as a small blue
// CircleMarker, refetched whenever the user pans or zooms. Hidden when
// zoom < MIN_ZOOM so we never paint thousands of markers at once (the
// dataset has ~110k city-wide). The dispatcher zooms in when reading
// cross-streets / sizing up a fire — that's the operationally useful
// moment to surface hydrant locations.
const MIN_ZOOM = 15;          // ~6-block radius
const FETCH_DEBOUNCE_MS = 250;
const MARKER_OPTIONS = {
  radius: 4,
  pathOptions: {
    color: "#0044CC",         // border (deeper blue)
    fillColor: "#3388FF",     // fill (Leaflet default blue, good contrast on dark tiles)
    weight: 1.5,
    fillOpacity: 0.95,
    opacity: 1,
  },
};

export function HydrantLayer() {
  const map = useMap();
  const [hydrants, setHydrants] = useState([]);
  const [zoom, setZoom] = useState(map.getZoom());
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const refetch = () => {
    const z = map.getZoom();
    setZoom(z);
    if (z < MIN_ZOOM) {
      // Out of operational range — drop the layer entirely so the leaflet
      // pane has zero markers when the dispatcher is panning the city.
      if (hydrants.length) setHydrants([]);
      return;
    }
    const b = map.getBounds();
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    axios
      .get(`${API}/hydrants`, {
        params: {
          min_lat: b.getSouth(),
          max_lat: b.getNorth(),
          min_lng: b.getWest(),
          max_lng: b.getEast(),
          limit: 1500,
        },
        signal: controller.signal,
      })
      .then((r) => setHydrants(Array.isArray(r.data) ? r.data : []))
      .catch(() => { /* ignore aborts and transient errors */ });
  };

  const scheduleRefetch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(refetch, FETCH_DEBOUNCE_MS);
  };

  useMapEvents({ moveend: scheduleRefetch, zoomend: scheduleRefetch });

  // Initial fetch on mount — useMapEvents only fires on user actions.
  useEffect(() => {
    scheduleRefetch();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (zoom < MIN_ZOOM) return null;
  return (
    <>
      {hydrants.map((h) => (
        <CircleMarker
          key={h.id}
          center={[h.lat, h.lng]}
          radius={MARKER_OPTIONS.radius}
          pathOptions={MARKER_OPTIONS.pathOptions}
        />
      ))}
    </>
  );
}
